import classNames from 'classnames';
import type { FormEvent, JSX } from 'react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert, Button, toast } from '@feathr/components';
import { getQuery } from '@feathr/hooks';

import { FeathrLockup, FeathrMark } from './BrandMarks';
import { isExpired, parseChallenge } from './challenge';
import { FIXTURE_NAMES, getFixture } from './fixtures';
import { describeScopes } from './scopeLabels';
import type { IConsentAccount, IConsentChallenge } from './types';

import * as styles from './ConsentPage.css';

const MIDNIGHT_MUTED = '#5a6678';
const VIOLET = '#7c3aed';
// React escapes the rendered text; i18next escaping it too shows `&amp;` for a name like "AT&T".
const NO_ESCAPE = { escapeValue: false };

function CheckIcon({ className }: Readonly<{ className: string }>): JSX.Element {
  return (
    <svg
      aria-hidden={true}
      className={className}
      focusable={'false'}
      height={15}
      viewBox={'0 0 24 24'}
      width={15}
    >
      <path
        d={'m5 13 4 4L19 7'}
        fill={'none'}
        stroke={'currentColor'}
        strokeLinecap={'round'}
        strokeLinejoin={'round'}
        strokeWidth={2.6}
      />
    </svg>
  );
}

/**
 * Neutral placeholder for the partner. The page never loads a partner logo,
 * because the challenge claims are unverified here.
 */
function ClientGlyph(): JSX.Element {
  return (
    <span className={styles.glyph}>
      <svg aria-hidden={true} focusable={'false'} height={22} viewBox={'0 0 24 24'} width={22}>
        <rect
          fill={'none'}
          height={16}
          rx={4}
          stroke={'#8f99a8'}
          strokeWidth={2}
          width={16}
          x={4}
          y={4}
        />
      </svg>
    </span>
  );
}

interface IAccountFieldProps {
  accounts: IConsentAccount[];
  clientName: string;
  selectedId: string;
  disabled: boolean;
  onSelect: (id: string) => void;
}

/**
 * Adaptive account field.
 *
 * With one account there is nothing to decide, so it renders as static context
 * rather than a list of one — no radio, no hover, nothing to click. The picker
 * only appears when a real choice exists.
 */
function AccountField({
  accounts,
  clientName,
  selectedId,
  disabled,
  onSelect,
}: Readonly<IAccountFieldProps>): JSX.Element {
  const { t } = useTranslation();

  if (accounts.length === 1) {
    return (
      <div>
        <div className={styles.fieldLabel}>{t('Account')}</div>
        <div className={styles.accountStatic}>
          <FeathrMark className={styles.accountMark} fill={MIDNIGHT_MUTED} />
          <span className={styles.accountName}>{accounts[0].name}</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className={styles.fieldLabel}>{t('Account')}</div>
      <div aria-label={t('Account')} className={styles.accountList} role={'radiogroup'}>
        {accounts.map((account) => {
          const selected = account.id === selectedId;
          return (
            <button
              aria-checked={selected}
              className={classNames(styles.accountOption, {
                [styles.accountOptionSelected]: selected,
              })}
              disabled={disabled}
              key={account.id}
              onClick={(): void => onSelect(account.id)}
              role={'radio'}
              type={'button'}
            >
              <FeathrMark className={styles.accountMark} fill={selected ? VIOLET : '#8f99a8'} />
              <span className={styles.accountName}>{account.name}</span>
              <span className={classNames(styles.radio, { [styles.radioSelected]: selected })} />
            </button>
          );
        })}
      </div>
      <p className={styles.cardSubtitle}>
        {t('{{clientName}} will only be able to access the account you pick here.', {
          clientName,
          interpolation: NO_ESCAPE,
        })}
      </p>
    </div>
  );
}

/**
 * Dev-only strip identifying a fixture render and linking the other states.
 * Rendered on the error states too, so a fixture is never a dead end.
 */
function DevBanner({ fixture }: Readonly<{ fixture: string }>): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className={styles.devBanner}>
      <span>{t('Fixture preview — nothing is submitted.')}</span>
      {FIXTURE_NAMES.map((name) => (
        <a
          className={classNames(styles.devLink, { [styles.devLinkActive]: name === fixture })}
          href={`?fixture=${name}`}
          key={name}
        >
          {name}
        </a>
      ))}
    </div>
  );
}

interface IFallbackProps {
  title: string;
  body: string;
  fixture?: string;
}

function Fallback({ title, body, fixture }: Readonly<IFallbackProps>): JSX.Element {
  return (
    <div className={styles.root}>
      {fixture !== undefined && <DevBanner fixture={fixture} />}
      <header className={styles.header}>
        <FeathrLockup className={styles.lockup} />
      </header>
      <div className={styles.fallback}>
        <h1 className={styles.fallbackTitle}>{title}</h1>
        <p className={styles.fallbackBody}>{body}</p>
      </div>
    </div>
  );
}

/**
 * OAuth consent screen.
 *
 * Renders the challenge estuary redirected here with, and posts the
 * user's decision straight back to estuary as a native form submission — not
 * fetch. That is deliberate: a form POST needs no CORS preflight and no
 * cross-origin credentials, and estuary answers with a 302 to the partner's
 * redirect_uri, so the browser follows it without this page having to handle
 * the redirect itself.
 */
function ConsentPage(): JSX.Element {
  const { t } = useTranslation();
  const { challenge: token, fixture } = getQuery<{ challenge: string; fixture: string }>();

  // Dev fixtures let the screen be reviewed before estuary can mint a real
  // challenge. `getFixture` returns undefined in production builds.
  const fixtureChallenge = useMemo(() => (fixture ? getFixture(fixture) : undefined), [fixture]);
  const isFixture = fixtureChallenge !== undefined;

  const challenge: IConsentChallenge | undefined = useMemo(
    () => fixtureChallenge ?? (token ? parseChallenge(token) : undefined),
    [fixtureChallenge, token],
  );

  const [selectedId, setSelectedId] = useState<string>('');
  const [submitting, setSubmitting] = useState<'allow' | 'deny' | undefined>(undefined);
  const formRef = useRef<HTMLFormElement>(null);

  const defaultAccountId =
    challenge?.accounts.find((account) => account.id === challenge.default_account_id)?.id ??
    challenge?.accounts[0]?.id ??
    '';
  const accountId = selectedId || defaultAccountId;

  /*
   * Submit is driven from an effect rather than from the buttons directly, and
   * the decision lives in state rather than in the DOM. Both are deliberate.
   *
   * The obvious approach — a hidden input with `defaultValue`, mutated by a ref
   * in onClick, with native submit buttons — is quietly broken. Any re-render
   * between the click and the browser serializing the form resets an
   * uncontrolled input back to its defaultValue, so the decision reverts to
   * `deny` and estuary answers `access_denied`. That is exactly what happened
   * here, and jsdom does not reproduce it unless the re-render is in play.
   *
   * Driving it from an effect means the decision is committed and rendered into
   * a controlled input *before* anything submits, so what posts is always what
   * was clicked. The challenge is single-use, so this runs once per decision.
   */
  useEffect(() => {
    if (submitting !== undefined) {
      formRef.current?.requestSubmit();
    }
  }, [submitting]);

  // A page restored from the back/forward cache keeps its spinning, disabled buttons.
  useEffect(() => {
    function handlePageShow(event: PageTransitionEvent): void {
      if (event.persisted) {
        setSubmitting(undefined);
      }
    }

    window.addEventListener('pageshow', handlePageShow);
    return (): void => window.removeEventListener('pageshow', handlePageShow);
  }, []);

  if (!challenge) {
    return (
      <Fallback
        body={t(
          'This authorization link is missing or malformed. Start the connection again from the app you were using.',
        )}
        fixture={isFixture ? fixture : undefined}
        title={t("We couldn't read this authorization request")}
      />
    );
  }

  if (isExpired(challenge)) {
    return (
      <Fallback
        body={t(
          'Authorization requests expire after a few minutes. Start the connection again from the app you were using.',
        )}
        fixture={isFixture ? fixture : undefined}
        title={t('This authorization request expired')}
      />
    );
  }

  if (challenge.accounts.length === 0) {
    return (
      <Fallback
        body={t(
          'Your user account is not a member of any Feathr account, so there is nothing to connect. Ask an administrator to add you to an account.',
        )}
        fixture={isFixture ? fixture : undefined}
        title={t('No account available to connect')}
      />
    );
  }

  const { client, accounts, scopes } = challenge;
  const scopeList = describeScopes(scopes, t);

  // A fixture has no challenge estuary could verify, so it reports the decision
  // instead of posting it.
  function decide(decision: 'allow' | 'deny'): void {
    if (isFixture) {
      toast(`Fixture: would POST decision=${decision}, account_id=${accountId}`, { type: 'info' });
      return;
    }
    setSubmitting(decision);
  }

  // Guards an Enter keypress on a fixture too.
  function handleFormSubmit(event: FormEvent<HTMLFormElement>): void {
    if (isFixture) {
      event.preventDefault();
    }
  }

  return (
    <div className={styles.root}>
      {isFixture && <DevBanner fixture={fixture} />}

      <header className={styles.header}>
        <FeathrLockup className={styles.lockup} />
      </header>

      <div className={styles.body}>
        <div className={styles.pitch}>
          <div className={styles.glyphPair}>
            <ClientGlyph />
            <span aria-hidden={true} className={styles.glyphDots}>
              •••
            </span>
            <span className={styles.glyph}>
              <FeathrMark className={styles.glyphMark} />
            </span>
          </div>

          <h1 className={styles.title}>
            {t('{{clientName}} would like access to your Feathr account', {
              clientName: client.name,
              interpolation: NO_ESCAPE,
            })}
          </h1>

          <Alert title={t('Only authorize apps you trust')} type={'warning'}>
            {t(
              'Allow access only if you started this connection from {{clientName}} and you recognize the address below. {{clientName}} will use your data under its own terms, and you can disconnect it at any time from Account Settings.',
              { clientName: client.name, interpolation: NO_ESCAPE },
            )}
          </Alert>

          <p className={styles.redirectNote}>
            {t('Allowing will return you to {{clientName}} at ', {
              clientName: client.name,
              interpolation: NO_ESCAPE,
            })}
            <span className={styles.redirectHost}>{client.redirect_host}</span>.
          </p>
        </div>

        <form
          action={`${ESTUARY_URL}/oauth/consent`}
          aria-label={t('Authorize {{clientName}}', {
            clientName: client.name,
            interpolation: NO_ESCAPE,
          })}
          className={styles.card}
          method={'post'}
          onSubmit={handleFormSubmit}
          ref={formRef}
        >
          <input name={'challenge'} type={'hidden'} value={token ?? ''} />
          <input name={'account_id'} type={'hidden'} value={accountId} />
          {/* Controlled, and defaulting to deny: a malfunction must never grant. */}
          <input name={'decision'} readOnly={true} type={'hidden'} value={submitting ?? 'deny'} />

          <div className={styles.cardHead}>
            <h2 className={styles.cardTitle}>
              {t('{{clientName}} will be able to', {
                clientName: client.name,
                interpolation: NO_ESCAPE,
              })}
            </h2>
            <p className={styles.cardSubtitle}>
              {t('These are the only permissions it is asking for.')}
            </p>
          </div>

          <ul className={styles.scopes}>
            {scopeList.map((scope) => (
              <li className={styles.scope} key={scope.key}>
                <CheckIcon
                  className={classNames(styles.scopeIcon, {
                    [styles.scopeIconWrite]: scope.kind === 'write',
                  })}
                />
                <span>{scope.label}</span>
              </li>
            ))}
          </ul>

          <div className={styles.rule} />

          <AccountField
            accounts={accounts}
            clientName={client.name}
            disabled={submitting !== undefined}
            onSelect={setSelectedId}
            selectedId={accountId}
          />

          <div className={styles.actions}>
            {/* type=button, not submit: the effect submits once the decision is
                committed to state, so what posts always matches what was clicked. */}
            <Button
              buttonType={'button'}
              className={styles.actionPrimary}
              disabled={submitting !== undefined}
              isLoading={submitting === 'allow'}
              onClick={(): void => decide('allow')}
              type={'primary'}
            >
              {t('Allow access')}
            </Button>
            <Button
              buttonType={'button'}
              disabled={submitting !== undefined}
              isLoading={submitting === 'deny'}
              onClick={(): void => decide('deny')}
              type={'secondary'}
            >
              {t('Cancel')}
            </Button>
          </div>
        </form>
      </div>

      <footer className={styles.footer}>
        <span>{t('© Feathr')}</span>
        <a className={styles.footerLink} href={'https://www.feathr.co/privacy-policy'}>
          {t('Privacy & terms')}
        </a>
      </footer>
    </div>
  );
}

export default ConsentPage;
